import { toDateString } from "views/app/FundValuationReportEdit/fundValuationPayload";
import { FAIR_VALUE_SOURCES, FAIR_VALUE_VIEWS, normalizeReport, toNumber } from "views/app/PortfolioSummary/fairValues";

// The fair value calculation of one fund (server docs/fund-fair-value.md, breakdown):
// report value + calls (+ capitalized interest) − returns − distributions = fair value.
// Pure logic, unit-tested without rendering.

// Transaction types per group; everything else does not change the fair value.
export const TRANSACTION_GROUPS = {
  calls: [2],
  capitalized_interest: [34],
  returns: [3, 41],
  distributions: [30, 32],
};
const GROUP_NAMES = Object.keys(TRANSACTION_GROUPS);

// The server's category when it sends one, otherwise by the transaction type.
export const transactionGroup = (transaction) => {
  if (GROUP_NAMES.includes(transaction?.category)) {
    return transaction.category;
  }
  const type = Number(transaction?.type);
  return GROUP_NAMES.find((group) => TRANSACTION_GROUPS[group].includes(type)) || null;
};

const toCents = (value) => Math.round(Math.abs(value) * 100);

const amountOf = (transaction) => transaction?.amount_converted ?? transaction?.amount ?? null;

// Sums of the groups as absolute values (cents, so they add up exactly).
export const groupTransactions = (transactions) => {
  const cents = { calls: 0, capitalized_interest: 0, returns: 0, distributions: 0 };
  (transactions || []).forEach((transaction) => {
    const group = transactionGroup(transaction);
    const amount = amountOf(transaction);
    if (group && amount !== null) {
      cents[group] += toCents(amount);
    }
  });
  const sums = {};
  GROUP_NAMES.forEach((group) => {
    sums[group] = cents[group] / 100;
  });
  return sums;
};

// The calculation lines of a view with a report, or null without one.
export const buildCalculation = (view) => {
  if (!view?.report) {
    return null;
  }
  const reportValue = view.report.market_value_converted;
  const sums = groupTransactions(view.transactions);
  const computed =
    reportValue === null
      ? null
      : (Math.round(reportValue * 100) +
          Math.round(sums.calls * 100) +
          Math.round(sums.capitalized_interest * 100) -
          Math.round(sums.returns * 100) -
          Math.round(sums.distributions * 100)) /
        100;
  return {
    report_value: reportValue,
    ...sums,
    show_capitalized_interest: sums.capitalized_interest !== 0,
    computed,
    // The server's value is authoritative: it rounds the report and the transactions
    // separately, so the computed sum may differ by a cent.
    fair_value: view.fair_value ?? computed,
  };
};

// Today as the local calendar day, never toISOString (a UTC shift would move the day).
export const defaultFairValueDate = (now = new Date()) => toDateString(now);

const normalizeTransaction = (transaction) => {
  const t = transaction || {};
  const type = t.type === null || t.type === undefined || t.type === "" ? null : Number(t.type);
  return {
    id: t.id ?? null,
    date: toDateString(t.date),
    type: Number.isNaN(type) ? null : type,
    category: GROUP_NAMES.includes(t.category) ? t.category : null,
    currency: t.currency || "",
    amount: toNumber(t.amount),
    amount_converted: toNumber(t.amount_converted),
    sign: Number(t.sign) > 0 ? 1 : Number(t.sign) < 0 ? -1 : 0,
  };
};

export const normalizeBreakdownView = (view) => {
  if (!view || typeof view !== "object") {
    return null;
  }
  return {
    fair_value: toNumber(view.fair_value),
    difference: toNumber(view.difference),
    source: FAIR_VALUE_SOURCES.includes(view.source) ? view.source : null,
    report: normalizeReport(view.report),
    transactions: (Array.isArray(view.transactions) ? view.transactions : []).map(normalizeTransaction),
  };
};

// The breakdown response, tolerant of null, "" and missing fields.
export const normalizeBreakdown = (data) => {
  const d = data && typeof data === "object" ? data : {};
  const portfolios = Array.isArray(d.portfolios) ? d.portfolios : [];
  return {
    date: toDateString(d.date),
    private_investment_fund: d.private_investment_fund ?? null,
    name: d.name || "",
    security_currency: d.security_currency || "",
    portfolios: portfolios.map((p) => {
      const views = p?.views && typeof p.views === "object" ? p.views : {};
      const normalized = {};
      FAIR_VALUE_VIEWS.forEach((name) => {
        normalized[name] = normalizeBreakdownView(views[name]);
      });
      return {
        portfolio: p?.portfolio ?? null,
        portfolio_name: p?.portfolio_name || "",
        currency: p?.currency || "",
        balance: toNumber(p?.balance),
        market_value: toNumber(p?.market_value),
        views: normalized,
      };
    }),
  };
};
